import {
  Card,
  CardActionArea,
  CardContent,
  styled,
  Box,
  Fade,
} from '@mui/material'
import { ReactNode, memo, useCallback, useMemo } from 'react'
import Image from 'next/image'
import { Text } from '@components/Helpers'
import { useTranslation } from 'react-i18next'
import { toNumber } from '@helium/spl-utils'
import { BN } from '@coral-xyz/anchor'
import { useTokenPrices } from '@hooks/useTokenPrices'
import OpenLinkIcon from '@assets/openLinkIcon.svg'
import { useConnection } from '@solana/wallet-adapter-react'

const Root = styled(Card)({
  borderColor: 'var(--dewi_colors_gray_8) !important',
  borderWidth: 2,
  transition: 'all .2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.03)',
  },
  position: 'relative',
})

const ActionArea = styled(CardActionArea)(() => ({
  position: 'relative',
  display: 'contents',
}))

const CardImage = styled(Image)(() => ({
  borderRadius: 500,
  height: 70,
}))

type TokenCardProps = {
  mint: string
  ata: string
  /** Raw on-chain amount. */
  balance: string | number
  decimals: number
  image: string
  symbol: string
  name: string
}

function TokenCardContent({ children }: { children: ReactNode }) {
  const StyledCardContent = styled(CardContent)(() => ({
    paddingTop: 0,
    paddingBottom: 10,
    paddingLeft: 10,
    paddingRight: 10,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  }))
  return <StyledCardContent>{children}</StyledCardContent>
}

function TokenCardText({ children }: { children: ReactNode }) {
  const TextContainer = styled(Box)({
    border: '1px solid #424242',
    backgroundColor: '#0E0F13',
    borderRadius: 5,
    padding: 10,
  })

  return (
    <TextContainer>
      <Text
        sx={{
          textAlign: 'center',
          fontWeight: 'normal',
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 1,
        }}
      >
        {children}
      </Text>
    </TextContainer>
  )
}

function TokenCard({
  mint,
  ata,
  balance,
  decimals,
  image,
  symbol,
  name,
}: TokenCardProps) {
  const { t } = useTranslation()
  // Monochrome theme: no per-token colour extraction (it also threw on
  // cross-origin logos in some browsers and crashed the page).
  const calculatedColor: string | undefined = undefined
  const calculatedGradient: string | undefined = undefined
  const { tokenPrices } = useTokenPrices()

  // Decimals arrive with the balance now, so there is no per-card mint fetch.
  const amount = useMemo(() => {
    try {
      return toNumber(new BN(balance.toString()), decimals ?? 9)
    } catch (error) {
      return 0
    }
  }, [balance, decimals])

  const fiatAmount = useMemo(() => {
    if (!amount || !tokenPrices) return undefined
    return amount * tokenPrices[mint]?.price
  }, [amount, tokenPrices, mint])

  const { connection } = useConnection()

  const isDevnet = useMemo(() => {
    return connection.rpcEndpoint.includes('devnet')
  }, [connection?.rpcEndpoint])

  const openExplorerLink = useCallback(() => {
    window.open(
      `https://explorer.solana.com/address/${mint}${isDevnet ? '?cluster=devnet' : ''}`,
      '_blank',
    )
  }, [isDevnet])

  return (
    <Fade in timeout={1000}>
      <Box>
        <Root
          sx={{
            background: calculatedGradient,
          }}
        >
          <ActionArea>
            <Box
              padding={0.5}
              position="absolute"
              top={-25}
              left={20}
              sx={{
                background: calculatedColor,
                borderRadius: 500,
              }}
            >
              <CardImage alt={`${name}`} width={70} height={70} src={image} />
            </Box>
            <Box height={150} display={'flex'}>
              <div className="p-3 flex flex-col justify-end w-full">
                <button
                  onClick={openExplorerLink}
                  className="hover:text-gray-1 text-gray-6 self-end absolute top-4 right-2"
                >
                  <OpenLinkIcon
                    style={{
                      width: '32px',
                      height: '32px',
                      color: 'inherit',
                    }}
                  />
                </button>

                <h5 className="text-gray-5 text-size-2 font-space text-left mt-12 w-full">
                  {`${amount?.toLocaleString()} `}
                  <span className="text-gray-1">{symbol}</span>
                </h5>
                <h5 className="text-gray-5 text-size-2 font-space text-left w-full">
                  {fiatAmount ? (
                    <>
                      {fiatAmount?.toLocaleString()}{' '}
                      <span className="text-gray-1">USD</span>
                    </>
                  ) : (
                    t('components.tokenCard.noPriceAvailable')
                  )}
                </h5>
              </div>
            </Box>
            <Fade in={true} timeout={1000}>
              <Box>
                <TokenCardContent>
                  <TokenCardText>{name}</TokenCardText>
                </TokenCardContent>
              </Box>
            </Fade>
          </ActionArea>
        </Root>
      </Box>
    </Fade>
  )
}

export default memo(TokenCard)
